import EditIcon from '@mui/icons-material/Edit';
import { Autocomplete, AutocompleteInputChangeReason, IconButton, TextField } from '@mui/material';
import { useDebounceCallback } from '@react-hook/debounce';
import React, { useCallback, useMemo, useRef, useState } from 'react';

import { IAddressSuggestion } from '../../../types/shipping';
import { formatSuggestionLabel } from '../../../util/address';
import { SmartyStreetsProvider } from '../../../util/SmartyStreetsProvider';

interface IProps {
  onSelect: (suggestion: IAddressSuggestion) => void;
  onReset?: () => void;
  label?: string;
  name: string;
  value?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  error?: string | string[];
  isRequired?: boolean;
}

export const AutocompleteInput: React.FC<IProps> = ({
  name,
  value: query = '',
  onChange,
  onBlur,
  onSelect,
  onReset,
  label,
  isRequired,
  error,
}) => {
  const providerRef = useRef(new SmartyStreetsProvider());
  const requestIdRef = useRef(0);
  const [suggestions, setSuggestions] = useState<IAddressSuggestion[]>([]);
  const [isReadonly, setReadonly] = useState(false);

  const queryAddress = useDebounceCallback(
    async (searchQuery: string) => {
      if (searchQuery.length < 3) {
        setSuggestions([]);

        return;
      }

      const requestId = ++requestIdRef.current;
      const result = await providerRef.current.query(searchQuery);

      if (requestId !== requestIdRef.current) {
        return;
      }
      setSuggestions(Array.isArray(result) ? result : []);
    },
    700,
    false,
  );

  const handleSelect = useCallback(
    (suggestion: IAddressSuggestion) => {
      // Discards a query already in flight so its response cannot reopen the list after the pick.
      requestIdRef.current += 1;
      setSuggestions([]);
      if (onReset) {
        setReadonly(true);
      }
      onSelect(suggestion);
    },
    [onReset, onSelect],
  );

  const handleReset = useCallback(() => {
    if (onReset) {
      setReadonly(false);
      onReset();
    }
  }, [onReset]);

  const handleInputChange = useCallback(
    (_event: React.SyntheticEvent, newValue: string, reason: AutocompleteInputChangeReason) => {
      // A pick echoes the formatted label back here as 'reset'; querying it would schedule a
      // debounced lookup that reopens the dropdown on the address just chosen.
      // The clear button bypasses onClose, so the open list has to be dropped here.
      if (reason === 'clear') {
        requestIdRef.current += 1;
        setSuggestions([]);
      }
      if (reason === 'input' || reason === 'clear') {
        queryAddress(newValue);
      }

      const syntheticEvent = {
        target: { name, value: newValue },
        currentTarget: { name, value: newValue },
      } as React.ChangeEvent<HTMLInputElement>;
      onChange(syntheticEvent);
    },
    [name, onChange, queryAddress],
  );

  const errorText = useMemo(() => {
    if (!error) {
      return undefined;
    }

    return Array.isArray(error) ? error.join(', ') : error;
  }, [error]);

  return (
    <Autocomplete
      freeSolo
      size="medium"
      options={suggestions}
      inputValue={query}
      open={suggestions.length > 0}
      onClose={() => setSuggestions([])}
      onInputChange={handleInputChange}
      onChange={(_event, newValue) => {
        if (newValue && typeof newValue !== 'string') {
          handleSelect(newValue);
        }
      }}
      getOptionLabel={(option) => (typeof option === 'string' ? option : formatSuggestionLabel(option))}
      filterOptions={(x) => x}
      renderInput={(params) => (
        <TextField
          {...params}
          name={name}
          label={label}
          required={isRequired}
          error={!!error}
          helperText={errorText}
          onBlur={onBlur}
          slotProps={{
            htmlInput: { ...params.inputProps, readOnly: isReadonly },
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {onReset && isReadonly && (
                    <IconButton onClick={handleReset} edge="end" size="small" title="Reset address" sx={{ mr: 1 }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
      sx={{ width: '100%' }}
    />
  );
};
